import { resolveElementFromPath } from '@src/utility/compositionSettings';
import { observer } from 'mobx-react-lite';

import { ImagePreset } from '../../ImagePreset';
import { PropertyCaption } from '../../PropertyCaption';

import type { ControlRenderProps, VideoControlSetting } from '../ControlTypes';
import type { ElementState } from '@src/renderer/ElementState';

/**
 * VideoControl
 * Renders a lightweight preview (VideoPreset) and delegates selection to the existing global video replacement modal.
 * On successful selection (modal closes with a new url), we call modifyProperty for each elementPath with the chosen url.
 * We listen to changes in the active element's source value to detect replacement rather than rewiring modal internals.
 */
export const VideoControl = observer(
  ({
    control,
    activeElement,
    modifyProperty,
    unifiedValue,
  }: ControlRenderProps<VideoControlSetting>) => {
    const initialUrl =
      typeof unifiedValue === 'string' ? unifiedValue : undefined;
    const videoElement = resolveElementFromPath(
      activeElement,
      control.elementPaths.length > 0 ? control.elementPaths[0] : [],
    ) as unknown as ElementState;

    const handleVideoSourceChange = (newUrl: string) => {
      modifyProperty(control.elementPaths, control.property, newUrl);
    };

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <PropertyCaption>{control.label}</PropertyCaption>
        <ImagePreset
          activeElement={videoElement}
          parentCompositionElement={activeElement}
          url={initialUrl || ''}
          onSourceChange={handleVideoSourceChange}
          isVideoControl={true}
        />
      </div>
    );
  },
);

export default VideoControl;
